// src/components/ui/Modal.js
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import './ui.css';

const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  subtitle,
  icon: Icon, 
  children, 
  maxWidth = '380px', 
  bg = '#0F172A',
  headerBg = '#1E293B',
  borderColor = 'rgba(255, 255, 255, 0.15)',
  titleColor = '#FFFFFF',
  subtitleColor = '#38BDF8',
  iconBg = 'rgba(56, 189, 248, 0.15)',
  iconBorder = 'rgba(56, 189, 248, 0.3)',
  iconColor = '#38BDF8',
  top = '0px',
  bottom = '0px',
  zIndex = 999999
}) => {
  
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'auto';
    return () => { document.body.style.overflow = 'auto'; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const modalUI = (
    <div
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        top: top, 
        left: 0, 
        right: 0, 
        bottom: bottom,
        zIndex: zIndex,
        background: 'rgba(11, 19, 41, 0.82)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        boxSizing: 'border-box'
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: maxWidth,
          background: bg,
          borderRadius: '24px',
          border: `1px solid ${borderColor}`,
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.75), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
          animation: 'zoomIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          color: titleColor,
          fontFamily: "'Plus Jakarta Sans', sans-serif"
        }}
      >
        {/* Header Region */}
        {(title || Icon) && (
          <div style={{
            padding: '16px 20px',
            background: headerBg,
            borderBottom: `1px solid ${borderColor}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {Icon && (
                <div style={{ 
                  width: '36px', height: '36px',
                  borderRadius: '12px',
                  background: iconBg, 
                  border: `1px solid ${iconBorder}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Icon size={18} color={iconColor} strokeWidth={2.5} />
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {title && (
                  <h3 style={{ fontSize: '13px', fontWeight: '900', color: titleColor, margin: 0, letterSpacing: '-0.2px', textTransform: 'uppercase' }}>
                    {title}
                  </h3>
                )}
                {subtitle && (
                  <span style={{ fontSize: '9px', fontWeight: '800', color: subtitleColor, letterSpacing: '0.4px' }}>
                    {subtitle}
                  </span>
                )}
              </div>
            </div>
            
            {/* Right-Aligned Close Button */}
            <button
              onClick={onClose}
              style={{
                background: '#0F172A', 
                border: '1px solid rgba(255, 255, 255, 0.12)', 
                borderRadius: '10px', 
                padding: '6px',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                cursor: 'pointer', 
                transition: 'all 0.2s',
                marginLeft: 'auto'
              }}
            >
              <X size={16} color="#94A3B8" strokeWidth={2.5} />
            </button>
          </div>
        )}

        {/* Content Region */}
        <div style={{ 
            padding: '20px', 
            overflowY: 'auto', 
            maxHeight: 'calc(80vh - 70px)',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            width: '100%'
        }}>
          {children}
        </div>
      </div>
    </div>
  );

  return createPortal(modalUI, document.body);
};

export default Modal;